import React, { useEffect, useState, useRef } from "react"
import {
  Platform,
  View,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Keyboard,
} from "react-native"
import { router } from "expo-router"
import { GestureHandlerRootView } from "react-native-gesture-handler"
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet"
import { useUser, useBuyBoxMutation } from "@/queries/user"
import {
  MIN_PRICES,
  MAX_PRICES,
  MIN_PROFITS,
  MIN_YEARS,
  MAX_YEARS,
  BEDS,
  BATHS,
  MIN_SQFTS,
  MAX_SQFTS,
} from "@/constants/BuyBox"
import CloseButton from "@/assets/icons/close-button.svg"
import FeaturePills from "@/components/buybox/FeaturePills"
import FeatureRange from "@/components/buybox/FeatureRange"
import FeatureSlider from "@/components/buybox/FeatureSlider"

import { buyBoxSubmitted, buyBoxViewed } from "@/analytics/events/buybox"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import BFText, { Small, H3 } from "@/components/standard/BFText"
import {
  oneSignalOptIn,
  requestOneSignalPermission,
  useOneSignalPermissions,
} from "@/stores/oneSignal"
import * as AccountEvents from "@/analytics/events/account"
import Bell from "phosphor-react-native/src/icons/Bell"
import { BFColors } from "@/constants/Colors"
import BuyBoxZipCodeInput from "@/components/buybox/BuyBoxZipCodeInput"
import { setBuyBoxSetUp } from "@/stores/myLeads"
import { showErrorToast } from "@/components/toasts/AppToast"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import { LightBackedBottomButton } from "@/components/modals/LightBackedBottomButton"

const Divider = () => <View className="flex h-px bg-light-grey" />

export default function BuyBox() {
  const { data: user } = useUser()
  const isDesktop = useIsDesktop()
  const buyBoxMutation = useBuyBoxMutation()
  const [minPrice, setMinPrice] = useState<string | number | null>(null)
  const [maxPrice, setMaxPrice] = useState<string | number | null>(null)
  const [minProfit, setMinProfit] = useState<string | number | null>(null)
  const [minArv, setMinArv] = useState<string | number | null>(null)
  const [maxArv, setMaxArv] = useState<string | number | null>(null)
  const [minYearBuilt, setMinYearBuilt] = useState<string | number | null>(null)
  const [maxYearBuilt, setMaxYearBuilt] = useState<string | number | null>(null)
  const [beds, setBeds] = useState<string[]>(["Any"])
  const [baths, setBaths] = useState<string[]>(["Any"])
  const [minSqft, setMinSqft] = useState<string | number | null>(null)
  const [maxSqft, setMaxSqft] = useState<string | number | null>(null)
  const [zipCodes, setZipCodes] = useState<string[]>([])
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)

  const scrollViewRef = useRef<ScrollView>(null)

  useViewedCallback(() => {
    setStatusBarStyle("dark")
    buyBoxViewed()
  })

  useEffect(() => {
    const showSubscription = Keyboard.addListener("keyboardWillShow", () => {
      setIsKeyboardVisible(true)
    })
    const hideSubscription = Keyboard.addListener("keyboardDidHide", () => {
      setIsKeyboardVisible(false)
    })

    return () => {
      showSubscription.remove()
      hideSubscription.remove()
    }
  }, [])

  useEffect(() => {
    if (user?.me?.buyBox) {
      setMinPrice(user.me.buyBox.priceMin || null)
      setMaxPrice(user.me.buyBox.priceMax || null)
      setMinProfit(user.me.buyBox.profitMin || null)
      setMinArv(user.me.buyBox.arvMin || null)
      setMaxArv(user.me.buyBox.arvMax || null)
      setMinYearBuilt(user.me.buyBox.yearBuiltMin || null)
      setMaxYearBuilt(user.me.buyBox.yearBuiltMax || null)
      setBeds(user.me.buyBox.beds ? user.me.buyBox.beds : ["Any"])
      setBaths(user.me.buyBox.baths ? user.me.buyBox.baths : ["Any"])
      setMinSqft(user.me.buyBox.areaMin || null)
      setMaxSqft(user.me.buyBox.areaMax || null)
      setZipCodes(user.me.buyBox.zipCodes || [])
    }
  }, [user])

  const onClearAll = () => {
    setMinPrice(null)
    setMaxPrice(null)
    setMinProfit(null)
    setMinArv(null)
    setMaxArv(null)
    setMinYearBuilt(null)
    setMaxYearBuilt(null)
    setBeds(["Any"])
    setBaths(["Any"])
    setMinSqft(null)
    setMaxSqft(null)
    setZipCodes([])
  }

  const onSave = () => {
    setBuyBoxSetUp(true)
    buyBoxSubmitted({
      priceMin: minPrice as number,
      priceMax: maxPrice as number,
      profitValue: minProfit as number,
      arvMin: minArv as number,
      arvMax: maxArv as number,
      yrBltMin: minYearBuilt as number,
      yrBltMax: maxYearBuilt as number,
      bedsValue: beds,
      bathsValue: baths,
      sqftMin: minSqft as number,
      sqftMax: maxSqft as number,
      zipCodes,
    })
    buyBoxMutation.mutate(
      {
        priceMin: minPrice as number,
        priceMax: maxPrice as number,
        profitMin: minProfit as number,
        arvMin: minArv as number,
        arvMax: maxArv as number,
        yearBuiltMin: minYearBuilt as number,
        yearBuiltMax: maxYearBuilt as number,
        beds,
        baths,
        areaMin: minSqft as number,
        areaMax: maxSqft as number,
        zipCodes,
      },
      {
        onError: (error) => {
          console.error(error)
          showErrorToast({
            title: "Error",
            message: `${String(error)}`,
          })
        },
      }
    )
    router.back()
  }

  const scrollToEnd = () => {
    scrollViewRef.current?.scrollToEnd({ animated: true })
  }

  return (
    <KeyboardAvoidingView
      testID={"buy-box-screen"}
      behavior="padding"
      className={"flex-1"}
      style={{
        maxWidth: isDesktop ? 800 : "100%", // Limit width if on desktop
        marginHorizontal: isDesktop ? "auto" : undefined, // Center content on desktop
      }}
    >
      <GestureHandlerRootView style={{ flex: 1, backgroundColor: "white" }}>
        <BottomSheetModalProvider>
          <View className="flex flex-col flex-1 bg-off-white p-6">
            <View
              className="flex items-end justify-end"
              style={{
                height: Platform.OS === "web" ? 20 : 70,
              }}
            >
              <TouchableOpacity
                testID={"close-button"}
                onPress={() => {
                  router.push("../")
                }}
              >
                <CloseButton width={13} height={13} />
              </TouchableOpacity>
            </View>
            <ScrollView
              ref={scrollViewRef}
              className="flex flex-col flex-1 pt-6 pb-0"
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              <View className="flex flex-row flex-1 items-center mb-6">
                <H3 className="text-midnight-blue mr-4">My Buy Box</H3>
                <TouchableOpacity
                  testID={"clear-all-button"}
                  onPress={onClearAll}
                >
                  <BFText className="text-calm-blue text-[14px] underline">
                    {/* TODO: Replace with UnderlinedActionButton */}
                    Clear All
                  </BFText>
                </TouchableOpacity>
              </View>
              <BuyBoxNotifications />
              <Divider />
              <FeatureRange
                title="Purchase Price"
                minValue={minPrice}
                maxValue={maxPrice}
                minOptions={MIN_PRICES}
                maxOptions={MAX_PRICES}
                onValuesChanged={(
                  minVal: string | number | null,
                  maxVal: string | number | null
                ) => {
                  setMinPrice(minVal)
                  setMaxPrice(maxVal)
                }}
              />
              <Divider />
              <FeatureRange
                title="ARV"
                minValue={minArv}
                maxValue={maxArv}
                minOptions={MIN_PRICES}
                maxOptions={MAX_PRICES}
                onValuesChanged={(
                  minVal: string | number | null,
                  maxVal: string | number | null
                ) => {
                  setMinArv(minVal)
                  setMaxArv(maxVal)
                }}
              />
              <Divider />
              <FeatureSlider
                title="Profit"
                value={minProfit}
                options={MIN_PROFITS}
                onValueChanged={setMinProfit}
              />
              <Divider />
              <FeatureRange
                title="Year Built"
                minValue={minYearBuilt}
                maxValue={maxYearBuilt}
                minOptions={MIN_YEARS}
                maxOptions={MAX_YEARS}
                onValuesChanged={(
                  minVal: string | number | null,
                  maxVal: string | number | null
                ) => {
                  setMinYearBuilt(minVal)
                  setMaxYearBuilt(maxVal)
                }}
              />
              <Divider />
              <FeaturePills
                title="Bed"
                values={beds}
                options={BEDS}
                onValuesChanged={setBeds}
              />
              <FeaturePills
                title="Bath"
                values={baths}
                options={BATHS}
                onValuesChanged={setBaths}
              />
              <Divider />
              <FeatureRange
                title="Square Feet"
                minValue={minSqft}
                maxValue={maxSqft}
                minOptions={MIN_SQFTS}
                maxOptions={MAX_SQFTS}
                onValuesChanged={(
                  minVal: string | number | null,
                  maxVal: string | number | null
                ) => {
                  setMinSqft(minVal)
                  setMaxSqft(maxVal)
                }}
              />
              <Divider />

              <BuyBoxZipCodeInput
                zipCodes={zipCodes}
                onZipCodesChange={(updatedZipCodes) => {
                  setZipCodes(updatedZipCodes)
                }}
                onSuggestionsChange={scrollToEnd}
              />

              <View className="flex h-[140px]" />
            </ScrollView>
            {!isKeyboardVisible && (
              <LightBackedBottomButton
                testID="save-button"
                title="Save"
                onPress={onSave}
              />
            )}
          </View>
        </BottomSheetModalProvider>
      </GestureHandlerRootView>
    </KeyboardAvoidingView>
  )
}

const BuyBoxNotifications = () => {
  const { hasPushPermission, hasUserOptIn } = useOneSignalPermissions()
  const { isWeb } = useDeviceAttributes()

  const handleEnableNotifications = async () => {
    // Request permissions
    const permissionGranted = await requestOneSignalPermission()
    if (permissionGranted) {
      // Opt-in to notifications
      oneSignalOptIn()
      AccountEvents.notificationsEnabled()

      console.log("Push notifications enabled successfully")
    } else {
      AccountEvents.notificationsDisabled()
      console.log("Push notifications permission denied")
    }
  }

  // Do not show the button if notifications are already enabled
  if (hasPushPermission || hasUserOptIn || isWeb) return null

  return (
    <View className="flex flex-row items-center justify-between mb-8">
      <Small className="text-midnight-blue">
        {`Allow push notifications\nfor My Leads`}
      </Small>
      <TouchableOpacity
        testID={"enable-notifications-button"}
        className="flex flex-row items-center border border-red-orange rounded-sm p-2"
        onPress={() => {
          void handleEnableNotifications()
        }}
      >
        <Bell size={18} color={BFColors.redOrange} />
        <Small className="text-red-orange">Get Notifications</Small>
      </TouchableOpacity>
    </View>
  )
}
